import {
  FETCH_CITY_EVENTS,
  FETCH_CITY_EVENTS_SUCCESS,
  FETCH_CITY_EVENTS_FAILED,
  SAVE_CITY_EVENT,
  SAVE_CITY_EVENT_SUCCESS,
  SAVE_CITY_EVENT_FAILED,
  DELETE_CITY_EVENT_SUCCESS,
  FETCH_CITY_EVENT_SOURCES_SUCCESS,
  SAVE_CITY_EVENT_SOURCE_SUCCESS,
  DELETE_CITY_EVENT_SOURCE_SUCCESS,
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { get, update, push, query, orderByChild, startAt, limitToFirst, set, remove } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { cellOf } from '../other/demandHeatCodec';

// ─────────────────────────────────────────────────────────────────────────────
// City events — data contract (see CITY_EVENTS.md at the repo root)
//
//   /city_events/{id}
//     title        { en, am }                      am optional
//     category     'concert' | 'sport' | 'religious' | 'conference' | 'market'
//                  | 'holiday' | 'transport' | 'other'
//     venue        { name, nameAm, lat, lng, cell, placeKey }   cell = demand grid key or null
//     start, end   ms since epoch (entered as Addis wall time, UTC+3)
//     size         1 hundreds | 2 thousands | 3 tens of thousands
//     windows      { arrival: { before, after, weight }, exit: { before, after, weight } }  minutes / 0–1
//     image        { url, path, bytes } | null
//     notes, sourceUrl
//     source       { kind: 'manual' | 'calendar' | 'ics' | 'eventbrite' | 'predicthq' | 'apifootball' | 'aerodatabox', ref, feed }
//     dedupeKey    `${cell}|${startHour}|${normalised title}`
//     status       'draft' | 'pending' | 'published' | 'rejected' | 'cancelled'
//     createdBy, createdByName, createdAt, updatedBy, updatedAt,
//     submittedAt, approvedBy, approvedByName, approvedAt, rejectedReason, cancelledAt
//     push         { sent, at }            server-owned (phase 3)
//     impact       { at, by, weeks, windows, hours, peak, verdict, cityRatio … }  server-owned (phase 5)
//
//   /city_events_index/{YYYY-MM-DD}/{id}: true   — PUBLISHED events by Addis day
//     (start day … end day, max 3), maintained by the same multi-path update so
//     the publisher / push schedulers read one tiny node per day instead of
//     scanning /city_events.
//
// Agents write drafts and submit (status → pending); only supervisors / admins
// can publish, reject, unpublish or cancel (enforced in database-rules.json).
// Reads are one-shot get() queries — never a listener.
// ─────────────────────────────────────────────────────────────────────────────

export const CITY_EVENT_STATUS = {
  DRAFT: 'draft', PENDING: 'pending', PUBLISHED: 'published', REJECTED: 'rejected', CANCELLED: 'cancelled',
};
export const CITY_EVENT_CATEGORIES = ['concert', 'sport', 'religious', 'conference', 'market', 'holiday', 'transport', 'other'];
export const CITY_EVENT_SOURCES = ['manual', 'calendar', 'ics', 'eventbrite', 'predicthq', 'apifootball', 'aerodatabox'];
export const CITY_EVENT_SIZES = [1, 2, 3];

// Demand windows per category, minutes relative to start (arrival) and end
// (exit); weight 0–1 is the share of a full crowd wave. Editable per event.
export const CITY_EVENT_WINDOW_DEFAULTS = {
  concert:    { arrival: { before: 60, after: 0,  weight: 0.5 }, exit: { before: 15, after: 45, weight: 1 } },
  sport:      { arrival: { before: 90, after: 0,  weight: 0.5 }, exit: { before: 10, after: 50, weight: 1 } },
  religious:  { arrival: { before: 45, after: 0,  weight: 0.3 }, exit: { before: 15, after: 45, weight: 1 } },
  conference: { arrival: { before: 60, after: 15, weight: 0.4 }, exit: { before: 20, after: 40, weight: 0.8 } },
  market:     { arrival: { before: 30, after: 60, weight: 0.5 }, exit: { before: 60, after: 30, weight: 0.7 } },
  holiday:    { arrival: { before: 90, after: 30, weight: 0.5 }, exit: { before: 30, after: 60, weight: 1 } },
  transport:  { arrival: { before: 0,  after: 0,  weight: 0 },   exit: { before: 0,  after: 30, weight: 1 } },
  other:      { arrival: { before: 45, after: 0,  weight: 0.4 }, exit: { before: 15, after: 45, weight: 0.8 } },
};

export const ADDIS_OFFSET_MS = 3 * 60 * 60e3; // UTC+3, no DST
const pad2 = (n) => (n < 10 ? '0' : '') + n;
export const addisDayKey = (ms) => {
  const d = new Date((Number(ms) || 0) + ADDIS_OFFSET_MS);
  return d.getUTCFullYear() + '-' + pad2(d.getUTCMonth() + 1) + '-' + pad2(d.getUTCDate());
};
// Addis wall-clock parts ↔ ms, independent of the browser's time zone.
export const msToAddisParts = (ms) => {
  const d = new Date((Number(ms) || 0) + ADDIS_OFFSET_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(), h: d.getUTCHours(), mi: d.getUTCMinutes() };
};
export const addisPartsToMs = (p) => Date.UTC(p.y, p.m - 1, p.d, p.h || 0, p.mi || 0) - ADDIS_OFFSET_MS;
// 'YYYY-MM-DDTHH:mm' (what <input type="datetime-local"> speaks) ↔ ms, as Addis time.
export const addisLocalToMs = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(s || ''));
  if (!m) return NaN;
  return addisPartsToMs({ y: +m[1], m: +m[2], d: +m[3], h: +m[4], mi: +m[5] });
};
export const msToAddisLocal = (ms) => {
  if (!Number.isFinite(Number(ms))) return '';
  const p = msToAddisParts(ms);
  return p.y + '-' + pad2(p.m) + '-' + pad2(p.d) + 'T' + pad2(p.h) + ':' + pad2(p.mi);
};

// Addis days an event spans (start day … end day), capped at 3 — multi-day
// fairs still index only their first days; the publisher reads by day.
export const cityEventDayKeys = (ev) => {
  const start = Number(ev && ev.start); const end = Number(ev && ev.end);
  if (!Number.isFinite(start)) return [];
  const days = [addisDayKey(start)];
  if (Number.isFinite(end) && end > start) {
    for (let t = start + 864e5; t <= end && days.length < 3; t += 864e5) {
      const k = addisDayKey(t);
      if (days.indexOf(k) < 0) days.push(k);
    }
    const last = addisDayKey(end);
    if (days.indexOf(last) < 0 && days.length < 3) days.push(last);
  }
  return days;
};

export const normaliseTitle = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9ሀ-፿]+/g, ' ').trim().slice(0, 40);
// Same venue cell + same Addis hour + similar title → the same event (used for
// the duplicate warning in the portal and by the auto-fetch importers).
export const cityEventDedupeKey = (ev) => {
  const cell = ev && ev.venue && ev.venue.cell ? ev.venue.cell : 'x';
  const p = msToAddisParts(ev && ev.start);
  const title = normaliseTitle(ev && ev.title && (ev.title.en || ev.title.am));
  return cell + '|' + p.y + '-' + pad2(p.m) + '-' + pad2(p.d) + 'T' + pad2(p.h) + '|' + title;
};

// Drop undefined values (RTDB rejects them) without touching nested nulls.
const clean = (obj) => {
  if (Array.isArray(obj)) return obj.map(clean);
  if (obj && typeof obj === 'object') {
    const out = {};
    Object.keys(obj).forEach((k) => { if (obj[k] !== undefined) out[k] = clean(obj[k]); });
    return out;
  }
  return obj;
};

// One-shot list: events starting from `fromMs` (default: 3 days ago) ordered by
// start, at most `limit` rows. Requires .indexOn start on /city_events.
export const fetchCityEvents = (opts) => async (dispatch) => {
  const { cityEventsRef } = firebase;
  const fromMs = opts && Number.isFinite(Number(opts.fromMs)) ? Number(opts.fromMs) : Date.now() - 3 * 864e5;
  const limit = opts && Number(opts.limit) > 0 ? Math.min(1000, Number(opts.limit)) : 300;
  dispatch({ type: FETCH_CITY_EVENTS, payload: null });
  try {
    const snap = await get(query(cityEventsRef, orderByChild('start'), startAt(fromMs), limitToFirst(limit)));
    const data = snap.val() || {};
    const list = Object.keys(data).map((id) => Object.assign({}, data[id], { id }));
    dispatch({ type: FETCH_CITY_EVENTS_SUCCESS, payload: { list, fetchedAt: Date.now() } });
    return { success: true, list };
  } catch (error) {
    dispatch({ type: FETCH_CITY_EVENTS_FAILED, payload: error.message });
    return { success: false, error: error.message };
  }
};

// Create or update. `opts.previous` is the stored copy (for index clean-up),
// `opts.posterBlob` an image Blob to upload first (JPEG, already compressed
// by the caller). The caller sets status / audit fields; this only stamps
// updatedAt, the grid cell and the dedupe key, then writes event + day index
// in ONE multi-path update.
// `opts.patch` (row actions) applies a few fields on top of the STORED node
// instead of the page's cached row. For any existing event the stored node is
// re-read first (one ~1 KB get, still no listener) and the write is refused
// when it changed since the caller's copy was loaded — the list is a one-shot
// read, so two staff can hold different copies and would otherwise overwrite
// each other's edits and leave /city_events_index pointing at the wrong days.
const coord = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));
// Only what the impact report measures (functions/common/cityEventsImpact.js):
// the window minutes (not their weights) and the measured area — the venue
// cell plus 1 ring of cells, 2 rings for tens of thousands (size 3).
// Same defaults and clamp as impactWindows() on the server (minutes 0–1440).
const IMPACT_WINDOW_DEFAULT_MIN = { arrival: { before: 45, after: 0 }, exit: { before: 15, after: 45 } };
const windowMinutes = (w, k, f) => {
  const v = ((w || {})[k] || {})[f];
  const n = v === undefined || v === null || v === '' ? NaN : Number(v);
  return Number.isFinite(n) ? Math.min(1440, Math.max(0, n)) : IMPACT_WINDOW_DEFAULT_MIN[k][f];
};
const windowsSame = (a, b) => ['arrival', 'exit'].every((k) => ['before', 'after'].every((f) => windowMinutes(a, k, f) === windowMinutes(b, k, f)));
const areaRings = (size) => (Number(size) >= 3 ? 2 : 1);
// Same start, end, window minutes, venue cell and area as the stored copy?
const sameImpactInputs = (ev, stored) => Boolean(stored)
  && Number(ev.start) === Number(stored.start) && Number(ev.end) === Number(stored.end)
  && areaRings(ev.size) === areaRings(stored.size)
  && ((ev.venue && ev.venue.cell) || null) === ((stored.venue && stored.venue.cell) || null)
  && windowsSame(ev.windows, stored.windows);

export const saveCityEvent = (input, opts) => async (dispatch) => {
  const { cityEventsRef, cityEventRef, cityEventImageRef, dbRootRef } = firebase;
  const o = opts || {};
  dispatch({ type: SAVE_CITY_EVENT, payload: null });
  try {
    const id = input.id || push(cityEventsRef).key;
    const now = Date.now();
    let stored = null;
    if (input.id) {
      stored = (await get(cityEventRef(id))).val();
      if (!stored) throw new Error('This event was deleted — refresh the list.');
      if (o.previous && Number(stored.updatedAt || 0) !== Number(o.previous.updatedAt || 0)) {
        throw new Error('This event changed since the list was loaded — refresh and review it again.');
      }
    }
    const base = o.patch ? Object.assign({}, stored || {}, o.patch) : input;
    const ev = Object.assign({}, base, { id, updatedAt: now });
    if (!ev.createdAt) ev.createdAt = (stored && stored.createdAt) || now;
    // Server-owned push stats (functions/common/cityEventsPush.js writes
    // /push without bumping updatedAt): always the stored subtree, never the
    // page's copy — and never seeded into a new / duplicated event.
    if (stored && stored.push !== undefined) ev.push = stored.push; else delete ev.push;
    const lat = coord(ev.venue && ev.venue.lat); const lng = coord(ev.venue && ev.venue.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('The venue needs coordinates — pick a place or drop a pin.');
    const cell = cellOf(lat, lng);
    ev.venue = Object.assign({}, ev.venue, { lat, lng, cell: cell ? cell.key : null });
    // The impact report (phase 5, functions/common/cityEventsImpact.js) is
    // server-owned too — kept only while what it measured is unchanged. New
    // times, window minutes, venue cell or area (crowd size into / out of
    // "tens of thousands") drop it, so the row reads "not computed" and the
    // nightly (or Recompute) measures the corrected event instead.
    if (stored && stored.impact !== undefined && sameImpactInputs(ev, stored)) ev.impact = stored.impact; else delete ev.impact;
    ev.dedupeKey = cityEventDedupeKey(ev);
    if (o.posterBlob) {
      const r = cityEventImageRef(id);
      await uploadBytesResumable(r, o.posterBlob, { contentType: o.posterBlob.type || 'image/jpeg' });
      const url = await getDownloadURL(r);
      ev.image = { url, path: 'city_events/' + id + '/poster', bytes: o.posterBlob.size || 0 };
    }
    const updates = {};
    updates['city_events/' + id] = clean(ev);
    const prevDays = stored && stored.status === CITY_EVENT_STATUS.PUBLISHED ? cityEventDayKeys(stored) : [];
    const newDays = ev.status === CITY_EVENT_STATUS.PUBLISHED ? cityEventDayKeys(ev) : [];
    prevDays.forEach((d) => { if (newDays.indexOf(d) < 0) updates['city_events_index/' + d + '/' + id] = null; });
    newDays.forEach((d) => { updates['city_events_index/' + d + '/' + id] = true; });
    await update(dbRootRef, updates);
    const saved = clean(ev);
    dispatch({ type: SAVE_CITY_EVENT_SUCCESS, payload: saved });
    return { success: true, id, event: saved };
  } catch (error) {
    dispatch({ type: SAVE_CITY_EVENT_FAILED, payload: error.message });
    return { success: false, error: error.message };
  }
};

// Drafts / rejected only (published events are cancelled, never deleted, so
// the day index and the audit trail stay intact).
export const deleteCityEvent = (ev) => async (dispatch) => {
  const { dbRootRef } = firebase;
  if (!ev || !ev.id) return { success: false, error: 'no id' };
  if (ev.status === CITY_EVENT_STATUS.PUBLISHED) return { success: false, error: 'published events must be cancelled' };
  try {
    const updates = {};
    updates['city_events/' + ev.id] = null;
    await update(dbRootRef, updates);
    dispatch({ type: DELETE_CITY_EVENT_SUCCESS, payload: ev.id });
    return { success: true };
  } catch (error) {
    dispatch({ type: SAVE_CITY_EVENT_FAILED, payload: error.message });
    return { success: false, error: error.message };
  }
};

// ── Auto events sources (phase 4, admin only; rules on /city_events_sources) ──
// One-shot read of the connector registry + ICS feeds the nightly job and the
// portal's "Run now" use. The job seeds the built-in rows on its first run.
export const fetchCityEventSources = () => async (dispatch) => {
  const { cityEventSourcesRef } = firebase;
  try {
    const snap = await get(cityEventSourcesRef);
    const data = snap.val() || {};
    const sources = {};
    Object.keys(data).forEach((id) => { if (id.charAt(0) !== '_') sources[id] = data[id]; });
    dispatch({ type: FETCH_CITY_EVENT_SOURCES_SUCCESS, payload: { sources, fetchedAt: Date.now(), meta: data._meta || null } });
    return { success: true, sources, meta: data._meta || null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Create an ICS feed (id = 'ics_' + push key) or patch any source row
// (enabled, name, url, venue). Partial update, never a whole-node set.
export const saveCityEventSource = (id, patch) => async (dispatch) => {
  const { cityEventSourcesRef, cityEventSourceRef } = firebase;
  try {
    const key = id || ('ics_' + push(cityEventSourcesRef).key);
    const data = clean(Object.assign({}, patch, { updatedAt: Date.now() }));
    if (id) await update(cityEventSourceRef(key), data);
    else await set(cityEventSourceRef(key), Object.assign({ kind: 'ics', enabled: true, createdAt: Date.now() }, data));
    const snap = await get(cityEventSourceRef(key));
    dispatch({ type: SAVE_CITY_EVENT_SOURCE_SUCCESS, payload: { id: key, source: snap.val() } });
    return { success: true, id: key };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteCityEventSource = (id) => async (dispatch) => {
  const { cityEventSourceRef } = firebase;
  if (!id || id === 'calendar' || id === 'weekly') return { success: false, error: 'built-in sources cannot be deleted' };
  try {
    await remove(cityEventSourceRef(id));
    dispatch({ type: DELETE_CITY_EVENT_SOURCE_SUCCESS, payload: id });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// City events for riders: one-shot read of the public projection
// /city_events_rider/s (functions/common/cityEventsRider.js writes it).
// → { s } (null when the node is empty) or { error: 'permission_denied' | 'read_failed' }.
export const fetchCityEventsRider = async () => {
  const { cityEventsRiderRef } = firebase;
  try {
    const snap = await get(cityEventsRiderRef);
    const v = snap.val();
    return { s: v && typeof v === 'object' ? v : null };
  } catch (e) {
    const text = String((e && (e.code || e.message)) || '');
    return { error: /permission/i.test(text) ? 'permission_denied' : 'read_failed' };
  }
};
